import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { cx } from './classNames';
import { Portal } from './Portal';
import './ui-overlay.css';

export type ToastTone = 'ok' | 'warn' | 'danger' | 'info';

export interface UiToastOptions {
  message: React.ReactNode;
  tone?: ToastTone;
  /** 見出し。省略すると message だけの 1 行トースト */
  title?: React.ReactNode;
  /** 自動で消えるまでの ms。null なら閉じるまで残す（danger の既定は長め） */
  duration?: number | null;
  /** 「元に戻す」のような 1 手 */
  action?: { label: string; onClick: () => void };
}

export interface UiToast extends UiToastOptions {
  id: string;
}

export interface UiToastApi {
  /** 出す。戻り値の id は dismiss に使える */
  show: (options: UiToastOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const ToastContext = createContext<UiToastApi | null>(null);

const TONE_ICON: Record<ToastTone, LucideIcon> = {
  ok: CircleCheck,
  warn: TriangleAlert,
  danger: CircleAlert,
  info: Info,
};

/** danger は読み飛ばされると困るので、既定でも長く残す */
const DEFAULT_DURATION: Record<ToastTone, number | null> = {
  ok: 4000,
  warn: 5000,
  danger: 7000,
  info: 4000,
};

export interface UiToastProviderProps {
  children: React.ReactNode;
  /** 同時に見せる最大数。超えたら古いものから落とす */
  max?: number;
}

export function UiToastProvider({ children, max = 3 }: UiToastProviderProps) {
  const [toasts, setToasts] = useState<UiToast[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const clearTimer = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [clearTimer],
  );

  const clear = useCallback(() => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current.clear();
    setToasts([]);
  }, []);

  const show = useCallback(
    (options: UiToastOptions) => {
      seq.current += 1;
      const id = `ui-toast-${seq.current}`;
      const tone = options.tone ?? 'info';
      const duration = options.duration === undefined ? DEFAULT_DURATION[tone] : options.duration;

      setToasts((prev) => {
        const next = [...prev, { ...options, tone, id }];
        // あふれた分は古い方から捨てる。タイマーも一緒に片付ける
        const overflow = next.slice(0, Math.max(0, next.length - max));
        overflow.forEach((t) => clearTimer(t.id));
        return next.slice(-max);
      });

      if (duration != null) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }

      return id;
    },
    [max, dismiss, clearTimer],
  );

  // アンマウント時に残ったタイマーを止める（テストの act 警告と実害の両方を避ける）
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const api = useMemo<UiToastApi>(() => ({ show, dismiss, clear }), [show, dismiss, clear]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useUiToast(): UiToastApi {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error('useUiToast は <UiToastProvider> の中でだけ使える');
  }
  return api;
}

interface ToastViewportProps {
  toasts: UiToast[];
  onDismiss: (id: string) => void;
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  if (toasts.length === 0) return null;

  return (
    <Portal>
      <div className="ui-toast-viewport" data-testid="ui-toast-viewport">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
    </Portal>
  );
}

interface ToastItemProps {
  toast: UiToast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const tone = toast.tone ?? 'info';
  const Icon = TONE_ICON[tone];
  // danger だけは読み上げを割り込ませる。他は順番待ちで十分
  const assertive = tone === 'danger';

  return (
    <div
      className={cx('ui-toast', `ui-toast--${tone}`)}
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      data-testid="ui-toast"
    >
      <span className="ui-toast__icon">
        <Icon aria-hidden="true" />
      </span>
      <div className="ui-toast__content">
        {toast.title != null && <div className="ui-toast__title">{toast.title}</div>}
        <div className="ui-toast__message">{toast.message}</div>
      </div>
      {toast.action && (
        <button
          type="button"
          className="ui-toast__action"
          onClick={() => {
            toast.action?.onClick();
            onDismiss(toast.id);
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" className="ui-overlay__close" onClick={() => onDismiss(toast.id)} aria-label="閉じる">
        <X aria-hidden="true" />
      </button>
    </div>
  );
}
